import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import i18next from 'i18next';
import { connect } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import * as actions from 'store/actions';
import PatientSearch from '../PatientSearch/index';
import PatientListView from '../PatientUnresolvedIssues/ListView/index';
import PatientGridView from '../PatientUnresolvedIssues/GridView/index';
import { InfoNew, List, Grid } from '../../../components/Icons';
import {
	ToggleButton,
	Pagination,
	SelectComponent,
	Spinner,
} from '../../../components';
import s from './style.module.scss';
import {
	errorsParams,
	modelsParams,
	unresolvedErrorsParams,
} from '../Config/paramsCreator';
import { pageOptions, patientDashboardErrorGroups } from '../Config/constants';

function PatientErrors(props) {
	const [showGrid, setShowList] = useState(false);
	const [selectedErrorType, setSelectedErrorType] = useState([]);
	const [selectedModelType, setSelectedModelType] = useState([]);
	const [pageSize, setPageSize] = useState(20);
	const [isOldestSorting, setIsOldestSorting] = useState(false);
	const [selectedRows, setSelectedRows] = useState([]);
	const [filterErrorOptions, setFilterErrorOptions] = useState([]);
	const [filterModelOptions, setFilterModelOptions] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [sortingOn, setSortingOn] = useState('happened_at');
	const [sortingDirection, setSortingDirection] = useState('desc');
	const [offSet, setOffSet] = useState(0);
	const [initializedErrorFromQuery, setInitializedErrorFromQuery] =
		useState(false);
	const [initializedModelFromQuery, setInitializedModelFromQuery] =
		useState(false);
	const [queryErrorId, setQueryErrorId] = useState(null);
	const [queryModelIds, setQueryModelIds] = useState([]);
	const location = useLocation();

	// Destructure specific props for stable hook dependencies
	const {
		errorTypeOptions,
		modelTypeOptions,
		getErrors,
		getModels,
		getUnresolvedErrors,
	} = props;
	const errorOptionsLength = errorTypeOptions?.length || 0;
	const modelOptionsLength = modelTypeOptions?.length || 0;

	// Request filter options once, independent of data fetch
	const requestedErrorsRef = React.useRef(false);
	const requestedModelsRef = React.useRef(false);

	useEffect(() => {
		if (!requestedErrorsRef.current && !errorOptionsLength) {
			requestedErrorsRef.current = true;
			getErrors({ params: errorsParams });
		}
	}, [errorOptionsLength, getErrors]);

	useEffect(() => {
		if (!requestedModelsRef.current && !modelOptionsLength) {
			requestedModelsRef.current = true;
			getModels({ params: modelsParams });
		}
	}, [modelOptionsLength, getModels]);

	// fltering error type options based on code_string
	useEffect(() => {
		filterErrorTypeOPtions(errorTypeOptions);
	}, [errorTypeOptions]);

	// fltering model type options
	useEffect(() => {
		filterModelTypeOPtions(modelTypeOptions || []);
	}, [modelTypeOptions]);

	// reset initialization flags and parse query on URL change
	useEffect(() => {
		setInitializedErrorFromQuery(false);
		setInitializedModelFromQuery(false);

		const params = new URLSearchParams(location.search);
		const errorId = params.get('error_id');
		const modelIdsParam =
			params.get('model_id') || params.get('concentrator_model_id');
		const modelIds = modelIdsParam
			? modelIdsParam
					.split(',')
					.map((s) => s.trim())
					.filter(Boolean)
			: [];

		setQueryErrorId(errorId ? String(errorId) : null);
		setQueryModelIds(modelIds);
	}, [location.search]);

	// initialize filters from query params (error_id, model_id)
	useEffect(() => {
		const errorId = queryErrorId;
		const modelIds = queryModelIds;

		// Initialize error filter when options are ready and not yet applied
		if (
			errorId &&
			filterErrorOptions.length > 0 &&
			!initializedErrorFromQuery
		) {
			const match = filterErrorOptions.find((opt) =>
				opt.value
					.split(',')
					.some((pair) => pair.split(':')[1] === String(errorId)),
			);
			if (match) {
				setSelectedErrorType([match.value]);
				setInitializedErrorFromQuery(true);
			}
		}

		// Initialize model filter(s) when options are ready and not yet applied
		if (
			modelIds.length > 0 &&
			filterModelOptions.length > 0 &&
			!initializedModelFromQuery
		) {
			const modelIdsNum = modelIds
				.map((id) => Number(id))
				.filter((n) => !Number.isNaN(n));
			const matchedIds = filterModelOptions
				.filter((opt) => modelIdsNum.includes(Number(opt.value)))
				.map((opt) => opt.value);
			if (matchedIds.length > 0) {
				setSelectedModelType(matchedIds);
			}
			// Always mark as initialized, even if not found
			setInitializedModelFromQuery(true);
		}
	}, [
		queryErrorId,
		queryModelIds,
		filterErrorOptions,
		filterModelOptions,
		initializedErrorFromQuery,
		initializedModelFromQuery,
	]);

	// Determine readiness to fetch data
	const hasQuery = Boolean(queryErrorId) || queryModelIds.length > 0;
	const optionsReady =
		filterErrorOptions.length > 0 && filterModelOptions.length > 0;
	const queryInitialized =
		(!queryErrorId || initializedErrorFromQuery) &&
		(queryModelIds.length === 0 || initializedModelFromQuery);
	const filtersReady =
		(!hasQuery && optionsReady) ||
		(hasQuery && optionsReady && queryInitialized);

	// fetching unresolved errors based on selected filters,pageSize, sorting and pagination and updating the table
	useEffect(() => {
		if (!filtersReady) return;

		const params = {
			...unresolvedErrorsParams,
			limit: pageSize,
			orderBy: sortingOn,
			sortedBy: sortingDirection,
			offset: offSet,
		};
		let errorCodes = [];
		let errorIds = [];
		if (selectedErrorType.length > 0) {
			selectedErrorType.forEach((item) => {
				let codeString = item.split(',');
				codeString.forEach((i) => {
					let parts = i.split(':');
					if (parts.length > 1) {
						let code = parts[0];
						let id = parts[1];
						errorCodes.push(code);
						errorIds.push(id);
					}
				});
			});
		}
		const uniqueErrorIds = errorIds.filter(
			(item, index) => errorIds.indexOf(item) === index,
		);

		if (selectedErrorType.length > 0) {
			params.searchFields = {
				error_code: 'in',
			};
			params.search = {
				error_code: errorCodes.join(','),
				error_id: uniqueErrorIds.join(','),
			};
		}
		if (selectedModelType.length > 0) {
			params.searchFields = {
				concentrator_model_id: 'in',
			};
			params.search = {
				concentrator_model_id: selectedModelType.join(','),
			};
		}
		if (selectedErrorType.length > 0 && selectedModelType.length > 0) {
			params.searchFields = {
				error_code: 'in',
				concentrator_model_id: 'in',
			};
			params.search = {
				error_code: errorCodes.join(','),
				error_id: uniqueErrorIds.join(','),
				concentrator_model_id: selectedModelType.join(','),
			};
		}

		getUnresolvedErrors({ params: params });
	}, [
		filtersReady,
		sortingOn,
		sortingDirection,
		selectedErrorType,
		selectedModelType,
		pageSize,
		offSet,
		getUnresolvedErrors,
	]);

	// handle of toggle button switch
	const handleSortingToggle = () => {
		const sortingDirection = isOldestSorting ? 'desc' : 'asc';
		setSortingDirection(sortingDirection);
		setIsOldestSorting(!isOldestSorting);
	};

	// fltering error type options based on code_string
	const filterErrorTypeOPtions = (errorOptions) => {
		const filteredData = errorOptions?.filter((item) =>
			patientDashboardErrorGroups.includes(item.code_string),
		);
		const combinedArray = filteredData?.reduce((eachItem, currentItem) => {
			const existingObjIndex = eachItem.findIndex(
				(obj) => obj.code_string === currentItem.code_string,
			);

			if (existingObjIndex !== -1) {
				eachItem[existingObjIndex].value += `,${
					currentItem.code + ':' + currentItem.id
				}`;
			} else {
				eachItem.push({
					code: currentItem.code,
					code_string: currentItem.code_string,
					value: currentItem.code + ':' + currentItem.id,
					concentrator_model_id: currentItem.concentrator_model_id,
					label: currentItem.description,
					description_engineer: currentItem.description_engineer,
					id: currentItem.id,
					type: currentItem.type,
				});
			}
			return eachItem;
		}, []);
		errorOptions = combinedArray;
		setFilterErrorOptions(errorOptions);
	};

	// fltering model type options
	const filterModelTypeOPtions = (modelOptions) => {
		const modelUpdatedOption = modelOptions.map((currentItem) => {
			return {
				value: currentItem.id,
				label: currentItem.description,
			};
		});
		setFilterModelOptions(modelUpdatedOption);
	};

	// handle of sorting type change from the table header in PatientListView
	const handleSortTypeChange = (sortOn, sortDirection) => {
		setSelectedRows([]);
		setSortingOn(sortOn);
		setSortingDirection(sortDirection);
	};

	// handle of error type change from the filter
	const handleErrorTypeChange = (value) => {
		try {
			setSelectedErrorType(value);
			setSelectedRows([]);
		} catch (e) {
			console.log(e.message);
		}
	};

	// handle of model type change from the filter
	const handleModelTypeChange = (value) => {
		try {
			setSelectedModelType(value);
			setSelectedRows([]);
		} catch (e) {
			console.log(e.message);
		}
	};

	// handle of page size change from the pagination
	const handlePageSizeChange = (value) => {
		try {
			setSelectedRows([]);
			setOffSet(0);
			setPageSize(value);
			setCurrentPage(1);
		} catch (e) {
			console.log(e.message);
		}
	};

	// handle of selected rows change from the table to be resolved
	const updateSelectedRows = (errorIds) => {
		setSelectedRows(errorIds);
	};

	// handle of page number change from the pagination
	const handlePageNumberChange = (pageNumber) => {
		setCurrentPage(pageNumber);
		setOffSet(pageNumber === 1 ? 0 : (pageNumber - 1) * pageSize);
	};

	let errorData = filterErrorOptions.map((item) => {
		return {
			...item,
			label: i18next.t(item.code_string),
		};
	});
	let errorDescription = props?.unresolvedError?.data?.map((item) => {
		return {
			...item,
			error_description: i18next.t(item.error_description),
		};
	});

	const sortingClass = [s.sortingTypography, s.active];
	return (
		<div className={s.patientWrapper}>
			<div className={s.patientContainer}>
				<div>
					{/* <h1 className={s.titlestyle}>{i18next.t('patient_dashboard')}</h1> */}
					<div className={s.patientSearchstyle}>
						<PatientSearch />
					</div>
				</div>
				<div className={s.patientliststyle}>
					<div className={s.title_align}>
						<div className={s.filterAlign}>
							<div className={s.dashboadIssue}>
								<span>{i18next.t('unresolved_issue')}</span>
								<InfoNew data-tip='' data-for='dashboardError' />
								<ReactTooltip
									id='dashboardError'
									className={s.tooltip}
									place='bottom'
									type='light'
									effect='solid'
								>
									<p>{i18next.t('errorDashboardMessage')}</p>
								</ReactTooltip>
							</div>
							<div className={s.switch}>
								<div className={s.filter_options}>
									<SelectComponent
										key='errorType'
										isMulti={true}
										options={errorData}
										value={selectedErrorType}
										onChange={handleErrorTypeChange}
										placeholder={i18next.t('error_type_all')}
										className={s.selectfilterStyling}
									/>
									<SelectComponent
										key='modelType'
										isMulti={true}
										options={filterModelOptions}
										value={selectedModelType}
										onChange={handleModelTypeChange}
										placeholder={i18next.t('model_type_all')}
										className={s.selectfilterStyling}
									/>
								</div>
							</div>
						</div>
						<div className={s.filterAlign}>
							<span
								className={
									isOldestSorting ? s.sortingTypography : sortingClass.join(' ')
								}
								onClick={() => handleSortingToggle()}
							>
								{i18next.t('latest')}
							</span>
							<ToggleButton
								isOn={isOldestSorting}
								toggle={() => handleSortingToggle()}
							/>
							<span
								className={
									!isOldestSorting
										? s.sortingTypography
										: sortingClass.join(' ')
								}
								onClick={() => handleSortingToggle()}
							>
								{i18next.t('oldest')}
							</span>
							<List
								className={
									showGrid ? s.active_fill_color : s.inactive_fill_color
								}
								onClick={() => {
									setShowList(true);
									setSelectedRows([]);
								}}
							/>
							<div className={s.line}></div>
							<Grid
								className={
									!showGrid ? s.active_fill_color : s.inactive_fill_color
								}
								onClick={() => {
									setShowList(false);
									setSelectedRows([]);
								}}
							/>
						</div>
					</div>
					<div className={s.errorContentWrapper}>
						{props?.loading ? (
							<div className={s.loaderWrap}>
								<Spinner />
							</div>
						) : (
							<>
								{!showGrid && (
									<div className={s.heightPagination}>
										<PatientGridView
											data={errorDescription}
											pageSize={pageSize}
											updateSelectedRows={updateSelectedRows}
											selectedRows={selectedRows}
											loading={false}
										/>
									</div>
								)}
								{showGrid && (
									<div>
										<PatientListView
											data={errorDescription}
											pageSize={pageSize}
											updateSelectedRows={updateSelectedRows}
											selectedRows={selectedRows}
											handleSortTypeChange={handleSortTypeChange}
											loading={false}
										/>
									</div>
								)}
							</>
						)}
					</div>
					{!props?.loading && props?.unresolvedError?.total_rows > 0 && (
						<Pagination
							options={pageOptions}
							pageSize={pageSize}
							currentPage={currentPage}
							endIndex={pageSize}
							onPageChange={handlePageNumberChange}
							totalCount={props?.unresolvedError?.total_rows}
							handlePageSize={handlePageSizeChange}
							loading={false}
						/>
					)}
				</div>
			</div>
		</div>
	);
}

const mapStateToProps = (state) => {
	return {
		errorTypeOptions: state.errors.data,
		modelTypeOptions: state.models.data,
		loading: state.unresolvedErrors.loading,
		unresolvedError: state.unresolvedErrors.unresolvedErrors,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		getProfiles: (options, isMerge) =>
			dispatch(actions.getProfiles(options, isMerge)),
		getUnresolvedErrors: (args) => dispatch(actions.getUnresolvedErrors(args)),
		getErrors: (args) => dispatch(actions.getErrors(args)),
		getModels: (args) => dispatch(actions.getModels(args)),
		resolveDashboardErros: (args) =>
			dispatch(actions.resolveDashboardErrors(args)),
	};
};

export default connect(
	mapStateToProps,
	mapDispatchToProps,
)(withTranslation()(PatientErrors));
